<template>
    <!-- Desktop Version -->
    <div v-if="!isMobile" class="chat-app"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        :class="{ 'drag-over': isDragging }">
        <!-- Main Chat Area -->
        <div class="chat-main">
            <!-- Welcome Screen (when no conversation started) -->
            <div v-if="messages.length <= 1 && !isTyping" class="welcome-screen">
                <div class="welcome-content">
                    <div class="welcome-avatar">
                        <span class="mdi mdi-robot-outline"></span>
                    </div>
                    <h1 class="welcome-title">Hi{{ username ? ' ' + username.split(' ')[0] : '' }}!</h1>
                    <p class="welcome-subtitle">How can I help you today?</p>
                </div>
            </div>

            <!-- Messages Area (when conversation started) -->
            <div v-else class="messages-area" ref="scrollContainer">
                <div class="messages-inner">
                    <div v-for="(message, index) in messages" :key="index"
                        :class="['message-row', message.from === 'user' ? 'user' : 'assistant']">
                        <div class="message-content">
                            <div v-if="message.from !== 'user'" class="avatar">
                                <span class="mdi mdi-robot-outline"></span>
                            </div>
                            <div class="message-text" v-html="message.text"></div>
                        </div>
                    </div>

                    <!-- Typing Indicator -->
                    <div v-if="isTyping" class="message-row assistant">
                        <div class="message-content">
                            <div class="avatar">
                                <span class="mdi mdi-robot-outline"></span>
                            </div>
                            <div class="typing-indicator">
                                <span class="typing-text">{{ typingText }}</span>
                                <div class="typing-dots">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bottom"></div>
                </div>
            </div>

            <!-- Input Area - Fixed at Bottom -->
            <div class="input-area">
                <div class="input-wrapper">
                    <!-- Toolbar Row: Model Selector + Active Features -->
                    <div class="input-toolbar">
                        <!-- Model Selector -->
                        <v-menu :close-on-content-click="true">
                            <template v-slot:activator="{ props }">
                                <button v-bind="props" class="toolbar-model-btn" :disabled="deepResearch || use_flowcase_bot || use_company_context_addon || use_pptx_agent">
                                    <span class="mdi mdi-robot-outline"></span>
                                    <span>{{ models.split('(')[0].trim() }}</span>
                                    <span class="model-version">{{ models.match(/\(([^)]+)\)/)?.[1] || '' }}</span>
                                    <span class="mdi mdi-chevron-down"></span>
                                </button>
                            </template>
                            <div class="dropdown-menu model-menu">
                                <button class="dropdown-item" @click="models = 'Auto Reasoning (GPT-5.2)'" :class="{ active: models === 'Auto Reasoning (GPT-5.2)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Auto Reasoning</span>
                                            <span class="model-version-tag">GPT-5.2</span>
                                        </div>
                                        <span class="model-desc">Fast, context-rich reasoning</span>
                                    </div>
                                    <span v-if="models === 'Auto Reasoning (GPT-5.2)'" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="models = 'Open Source (GPT-OSS)'" :class="{ active: models === 'Open Source (GPT-OSS)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Open Source</span>
                                            <span class="model-version-tag">GPT-OSS</span>
                                        </div>
                                        <span class="model-desc">Fast and efficient</span>
                                    </div>
                                    <span v-if="models === 'Open Source (GPT-OSS)'" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="models = 'Deep Analysis (o3)'" :class="{ active: models === 'Deep Analysis (o3)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Deep Analysis</span>
                                            <span class="model-version-tag">o3</span>
                                        </div>
                                        <span class="model-desc">In-depth inquiries</span>
                                    </div>
                                    <span v-if="models === 'Deep Analysis (o3)'" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="models = 'Best knowledge (GPT-4o)'" :class="{ active: models === 'Best knowledge (GPT-4o)' }">
                                    <div class="model-info">
                                        <div class="model-header">
                                            <span class="model-name">Best Knowledge</span>
                                            <span class="model-version-tag">GPT-4o</span>
                                        </div>
                                        <span class="model-desc">General knowledge</span>
                                    </div>
                                    <span v-if="models === 'Best knowledge (GPT-4o)'" class="mdi mdi-check"></span>
                                </button>
                            </div>
                        </v-menu>

                        <!-- Active Features Chips -->
                        <div v-if="deepResearch" class="chip">
                            <span class="mdi mdi-magnify-scan"></span>
                            <span>Deep Research</span>
                            <button @click="toggleDeepResearch">&times;</button>
                        </div>
                        <div v-if="use_flowcase_bot" class="chip">
                            <span class="mdi mdi-chart-box-outline"></span>
                            <span>References Bot</span>
                            <button @click="toggleReferenceBot">&times;</button>
                        </div>
                        <div v-if="use_company_context_addon" class="chip">
                            <span class="mdi mdi-link-variant"></span>
                            <span>Company Context</span>
                            <button @click="toggleCompanyContextAddon">&times;</button>
                        </div>
                        <div v-if="use_pptx_agent" class="chip">
                            <span class="mdi mdi-file-powerpoint-outline"></span>
                            <span>PowerPoint Agent</span>
                            <button @click="togglePptxAgent">&times;</button>
                        </div>
                        <div v-if="fileupload && !use_pptx_agent" class="chip file">
                            <span class="mdi mdi-file-pdf-box"></span>
                            <span>{{ filename }}</span>
                            <v-progress-circular v-if="!convertingPdfDone" indeterminate size="14" width="2" color="primary"></v-progress-circular>
                            <button v-else @click="onRemoveFile">&times;</button>
                        </div>
                        <div v-if="fileupload && use_pptx_agent" class="chip file pptx-context">
                            <span class="mdi mdi-file-pdf-box"></span>
                            <span>{{ filename }} (PPTX-Kontext)</span>
                            <v-progress-circular v-if="!convertingPdfDone" indeterminate size="14" width="2" color="primary"></v-progress-circular>
                            <button v-else @click="onRemoveFile">&times;</button>
                        </div>
                    </div>

                    <!-- Main Input Container -->
                    <div class="input-container">
                        <!-- Plus Button -->
                        <v-menu location="top start" :close-on-content-click="true">
                            <template v-slot:activator="{ props }">
                                <button v-bind="props" class="icon-btn plus-btn">
                                    <span class="mdi mdi-plus"></span>
                                </button>
                            </template>
                            <div class="dropdown-menu">
                                <button class="dropdown-item" @click="toggleDeepResearch" :class="{ active: deepResearch }">
                                    <span class="mdi mdi-magnify-scan"></span>
                                    <span>Deep Research</span>
                                    <span v-if="deepResearch" class="mdi mdi-check"></span>
                                </button>
                                <button v-if="has_flowcase_addon" class="dropdown-item" @click="toggleReferenceBot" :class="{ active: use_flowcase_bot }">
                                    <span class="mdi mdi-chart-box-outline"></span>
                                    <span>References Bot</span>
                                    <span v-if="use_flowcase_bot" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="toggleCompanyContextAddon" :class="{ active: use_company_context_addon }">
                                    <span class="mdi mdi-link-variant"></span>
                                    <span>Company Context</span>
                                    <span v-if="use_company_context_addon" class="mdi mdi-check"></span>
                                </button>
                                <button class="dropdown-item" @click="togglePptxAgent" :class="{ active: use_pptx_agent }">
                                    <span class="mdi mdi-file-powerpoint-outline"></span>
                                    <span>PowerPoint Agent</span>
                                    <span v-if="use_pptx_agent" class="mdi mdi-check"></span>
                                </button>
                                <div class="dropdown-divider"></div>
                                <button class="dropdown-item" @click="onUploadFile">
                                    <span class="mdi mdi-paperclip"></span>
                                    <span>Upload PDF</span>
                                </button>
                            </div>
                        </v-menu>

                        <!-- Text Input -->
                        <textarea
                            v-model="input"
                            :placeholder="isPrintingText ? 'Waiting for response...' : 'Message TIS...'"
                            :disabled="isPrintingText"
                            @keydown.enter.exact.prevent="sendMessage"
                            @input="autoResize"
                            ref="inputField"
                            rows="1"
                        ></textarea>

                        <!-- Send/Stop Button -->
                        <button v-if="!isPrintingText" class="icon-btn send-btn" @click="sendMessage" :disabled="!input.trim()">
                            <span class="mdi mdi-arrow-up"></span>
                        </button>
                        <button v-else class="icon-btn stop-btn" @click="stopTyping">
                            <span class="mdi mdi-stop"></span>
                        </button>
                    </div>
                </div>

                <!-- Footer -->
                <div class="input-footer">
                    <span>Powered by AI. Chat will be deleted after this session and will not be used for training.</span>
                    <span class="link" @click="overlayDisclaimer = true">Disclaimer</span>
                </div>
            </div>
        </div>

        <!-- Disclaimer Overlay -->
        <v-dialog v-model="overlayDisclaimer" max-width="800" scrollable>
            <v-card class="disclaimer-dialog">
                <div class="disclaimer-header">
                    <h2>Disclaimer</h2>
                </div>
                <div class="disclaimer-body">
                    <p class="lang-note">English version below</p>

                    <h3>1. Verwendungszweck und Status</h3>
                    <ul>
                        <li>Dieser digitale Assistent (Chatbot) wird von HPC bereitgestellt, um Mitarbeitende bei Recherchen, Ideenfindung und Textbausteinen zu unterstützen.</li>
                        <li>Das System befindet sich in einer Test-/Pilotphase und kann bei Bedarf eingeschränkt oder abgeschaltet werden.</li>
                    </ul>

                    <h3>2. Teilweise Web-Recherche</h3>
                    <ul>
                        <li>Um aktuelle Informationen einzuholen, nutzt der Chatbot bei Bedarf Bing Search innerhalb von Microsoft Azure. Dabei werden nur relevante Teilinhalte des Prompts an die Such-API übermittelt.</li>
                        <li>Eine vollständige Weitergabe der gesamten Unterhaltung an die Web-Suche erfolgt nicht.</li>
                    </ul>

                    <h3>3. Zulässige Eingaben</h3>
                    <ul>
                        <li><strong>Erlaubt:</strong> Interne Projektnamen, Kunden-/Kollegen-Namen, geschäftliche Daten, soweit für die Arbeit erforderlich.</li>
                        <li><strong>Bitte meiden:</strong> Hochsensible Daten (gemäß DSGVO Art. 9), z. B. Gesundheitsangaben, politische oder religiöse Überzeugungen u. Ä. Wenn absolut nötig, klären Sie das vorab mit Ihrer Datenschutzabteilung.</li>
                        <li><strong>Achtung bei streng vertraulichen Themen:</strong> Geben Sie hier nur das Nötigste ein. Bei Bedarf interne Kanäle (z. B. verschlüsselte Mails) nutzen.</li>
                    </ul>

                    <h3>4. Datenschutz & Verarbeitung</h3>
                    <ul>
                        <li><strong>Keine dauerhafte Speicherung:</strong> Eingaben werden nicht in einer HPC-eigenen Datenbank abgelegt. Sie verbleiben maximal 30 Tage in Azure-Logs zur Missbrauchsüberwachung.</li>
                        <li><strong>Keine Trainingsnutzung:</strong> Die Eingaben dienen nicht dem KI-Modelltraining. Microsoft/OpenAI hat darauf keinen Zugriff für Trainingszwecke.</li>
                        <li><strong>DSGVO-Konformität:</strong> HPC hat mit Microsoft Azure entsprechende Verträge (DPA) und richtet sich nach dem Prinzip der Datenminimierung.</li>
                    </ul>

                    <h3>5. Haftungsausschluss & Inhaltliche Grenzen</h3>
                    <ul>
                        <li><strong>Automatisierte Antworten:</strong> Der Chatbot generiert Texte mithilfe eines Sprachmodells und ggf. externer Webquellen. Diese können fehlerhaft, veraltet oder unvollständig sein.</li>
                        <li><strong>Keine verbindliche Beratung:</strong> Alle Ausgaben sind unverbindlich. Für kritische Entscheidungen ist eine fachliche Prüfung oder ein menschliches Review erforderlich.</li>
                        <li><strong>Haftung:</strong> HPC haftet nicht für Schäden, die aus dem Vertrauen auf Chatbot-Inhalte entstehen, soweit gesetzlich zulässig.</li>
                    </ul>

                    <h3>6. Web-Inhalte und Urheberrecht</h3>
                    <ul>
                        <li>Bei Web-Recherchen können urheberrechtlich geschützte Texte oder Ausschnitte zum Teil in die Antwort einfließen.</li>
                        <li>Wir bemühen uns, nur kurze Zitate oder Zusammenfassungen zu liefern. Eine kommerzielle oder unrechtmäßige Weiterverwendung liegt in der Verantwortung der Nutzenden.</li>
                    </ul>

                    <h3>7. Security & Missbrauch</h3>
                    <ul>
                        <li>Alle Datenverbindungen sind abgesichert, dennoch verbleibt ein Restrisiko (z. B. bei gezielten Angriffen).</li>
                        <li><strong>Missbrauch:</strong> Verstöße gegen geltendes Recht oder interne Firmenvorgaben (z. B. Eingabe sensibler Daten ohne Grund, Erzeugen schädlicher Inhalte) sind untersagt. HPC behält sich vor, Zugriffe einzuschränken oder zu sperren und ggf. weitere Schritte einzuleiten.</li>
                    </ul>

                    <h3>8. Konformität mit KI-Act und Data Act</h3>
                    <ul>
                        <li>HPC hat eine interne Risikobeurteilung durchgeführt (siehe interne Dokumentation) und berücksichtigt relevante Vorgaben des KI-Acts (Art. 9) sowie des EU Data Acts.</li>
                        <li>Aktuell keine Einstufung als „High Risk"; sollte sich der Einsatzbereich ändern (z. B. Personalentscheidungen, Finanz- oder Gesundheitsbereich), bedarf es einer erneuten Freigabe durch Compliance.</li>
                    </ul>

                    <h3>9. Änderungen und Kommunikation</h3>
                    <ul>
                        <li>HPC kann diesen Disclaimer jederzeit an neue technische, organisatorische oder rechtliche Anforderungen anpassen.</li>
                        <li>Über wesentliche Änderungen werden die Nutzenden informiert (Intranet, E-Mail o. Ä.).</li>
                    </ul>

                    <h3>10. Kontakt und Rückfragen</h3>
                    <p class="contact-block">
                        <strong>Allgemein/Sicherheit:</strong> Wir haben einen Datenschutzbeauftragten ernannt.<br>
                        Hamburger Hafen und Logistik Aktiengesellschaft<br>
                        Konzernbeauftragter für den Datenschutz<br>
                        Herr Dr. John Maurer<br>
                        Bei St. Annen 1<br>
                        20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>Mit der Nutzung dieses Assistenten stimmen Sie den oben genannten Bedingungen zu und nehmen sie zur Kenntnis.</strong></p>

                    <hr>

                    <h3>1. Purpose and Status</h3>
                    <ul>
                        <li>This digital assistant (chatbot) is provided by HPC to support employees in research, idea generation, and text modules.</li>
                        <li>The system is in a test/pilot phase and may be restricted or deactivated if necessary.</li>
                    </ul>

                    <h3>2. Partial Web Search</h3>
                    <ul>
                        <li>To retrieve up-to-date information, the chatbot uses Bing Search within Microsoft Azure when needed. Only relevant parts of the prompt are transmitted to the search API.</li>
                        <li>A complete transmission of the entire conversation to the web search does not take place.</li>
                    </ul>

                    <h3>3. Permitted Inputs</h3>
                    <ul>
                        <li><strong>Allowed:</strong> Internal project names, customer/colleague names, business data, as far as required for work purposes.</li>
                        <li><strong>Please avoid:</strong> Highly sensitive data (as per GDPR Art. 9), e.g., health information, political or religious beliefs, etc. If absolutely necessary, consult your data protection department beforehand.</li>
                        <li><strong>Caution with strictly confidential topics:</strong> Only enter what is necessary. Use internal channels (e.g., encrypted emails) if needed.</li>
                    </ul>

                    <h3>4. Data Protection & Processing</h3>
                    <ul>
                        <li><strong>No permanent storage:</strong> Inputs are not stored in an HPC-owned database. They remain in Azure logs for a maximum of 30 days for abuse monitoring.</li>
                        <li><strong>No training usage:</strong> Inputs are not used for AI model training. Microsoft/OpenAI has no access for training purposes.</li>
                        <li><strong>GDPR compliance:</strong> HPC has appropriate contracts (DPA) with Microsoft Azure and follows the principle of data minimization.</li>
                    </ul>

                    <h3>5. Liability Disclaimer & Content Limitations</h3>
                    <ul>
                        <li><strong>Automated responses:</strong> The chatbot generates text using a language model and, if applicable, external web sources. These may be incorrect, outdated, or incomplete.</li>
                        <li><strong>No binding advice:</strong> All outputs are non-binding. Critical decisions require expert review or human verification.</li>
                        <li><strong>Liability:</strong> HPC is not liable for damages resulting from reliance on chatbot-generated content, to the extent permitted by law.</li>
                    </ul>

                    <h3>6. Web Content and Copyright</h3>
                    <ul>
                        <li>Web searches may include copyrighted texts or excerpts in responses.</li>
                        <li>We strive to provide only short quotes or summaries. Users are responsible for any commercial or unlawful reuse.</li>
                    </ul>

                    <h3>7. Security & Misuse</h3>
                    <ul>
                        <li>All data connections are secured, but a residual risk remains (e.g., targeted attacks).</li>
                        <li><strong>Misuse:</strong> Violations of applicable laws or internal company policies (e.g., entering sensitive data without justification, generating harmful content) are prohibited. HPC reserves the right to restrict or block access and take further action if necessary.</li>
                    </ul>

                    <h3>8. Compliance with AI Act and Data Act</h3>
                    <ul>
                        <li>HPC has conducted an internal risk assessment (see internal documentation) and considers relevant requirements of the AI Act (Art. 9) and the EU Data Act.</li>
                        <li>Currently, no classification as "High Risk"; if the scope changes (e.g., personnel decisions, financial or healthcare applications), a new approval by Compliance is required.</li>
                    </ul>

                    <h3>9. Changes and Communication</h3>
                    <ul>
                        <li>HPC may adjust this disclaimer at any time to reflect new technical, organizational, or legal requirements.</li>
                        <li>Users will be informed of significant changes (Intranet, email, etc.).</li>
                    </ul>

                    <h3>10. Contact and Inquiries</h3>
                    <p class="contact-block">
                        <strong>General/Security:</strong> We have appointed a data protection officer.<br>
                        Hamburger Hafen und Logistik Aktiengesellschaft<br>
                        Konzernbeauftragter für den Datenschutz<br>
                        Herr Dr. John Maurer<br>
                        Bei St. Annen 1<br>
                        20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>By using this assistant, you agree to and acknowledge the above conditions.</strong></p>
                </div>
                <div class="disclaimer-footer">
                    <button class="primary-btn" @click="overlayDisclaimer = false">Close</button>
                </div>
            </v-card>
        </v-dialog>

        <!-- Hidden File Input -->
        <input type="file" ref="fileInput" style="display: none" accept="application/pdf" @change="handleFileUpload" />

        <!-- Loading Overlay -->
        <div v-if="loading" class="loading-overlay">
            <v-progress-circular color="primary" indeterminate size="48"></v-progress-circular>
        </div>
    </div>

    <!-- Mobile Version -->
    <div v-else class="chat-app mobile"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        :class="{ 'drag-over': isDragging }">
        <!-- Welcome Screen (when no conversation started) -->
        <div v-if="messages.length <= 1 && !isTyping" class="welcome-screen">
            <div class="welcome-content">
                <div class="welcome-avatar">
                    <span class="mdi mdi-robot-outline"></span>
                </div>
                <h1 class="welcome-title">Hi{{ username ? ' ' + username.split(' ')[0] : '' }}!</h1>
                <p class="welcome-subtitle">How can I help you today?</p>
            </div>
        </div>

        <!-- Messages Area (when conversation started) -->
        <div v-else class="messages-area" ref="scrollContainer">
            <div class="messages-inner">
                <div v-for="(message, index) in messages" :key="index"
                    :class="['message-row', message.from === 'user' ? 'user' : 'assistant']">
                    <div class="message-content">
                        <div v-if="message.from !== 'user'" class="avatar">
                            <span class="mdi mdi-robot-outline"></span>
                        </div>
                        <div class="message-text" v-html="message.text"></div>
                    </div>
                </div>

                <!-- Typing Indicator -->
                <div v-if="isTyping" class="message-row assistant">
                    <div class="message-content">
                        <div class="avatar">
                            <span class="mdi mdi-robot-outline"></span>
                        </div>
                        <div class="typing-indicator">
                            <span class="typing-text">{{ typingText }}</span>
                            <div class="typing-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bottom"></div>
            </div>
        </div>

        <!-- Mobile Input Area -->
        <div class="mobile-input-area">
            <div class="input-wrapper">
                <!-- Toolbar Row: Model Selector + Active Features -->
                <div class="input-toolbar">
                    <!-- Model Selector -->
                    <v-menu :close-on-content-click="true">
                        <template v-slot:activator="{ props }">
                            <button v-bind="props" class="toolbar-model-btn" :disabled="deepResearch || use_flowcase_bot || use_company_context_addon || use_pptx_agent">
                                <span class="mdi mdi-robot-outline"></span>
                                <span>{{ models.split('(')[0].trim() }}</span>
                                <span class="model-version">{{ models.match(/\(([^)]+)\)/)?.[1] || '' }}</span>
                                <span class="mdi mdi-chevron-down"></span>
                            </button>
                        </template>
                        <div class="dropdown-menu model-menu">
                            <button class="dropdown-item" @click="models = 'Auto Reasoning (GPT-5.2)'" :class="{ active: models === 'Auto Reasoning (GPT-5.2)' }">
                                <span>Auto Reasoning</span>
                                <span class="model-version-tag">GPT-5.2</span>
                                <span v-if="models === 'Auto Reasoning (GPT-5.2)'" class="mdi mdi-check"></span>
                            </button>
                            <button class="dropdown-item" @click="models = 'Open Source (GPT-OSS)'" :class="{ active: models === 'Open Source (GPT-OSS)' }">
                                <span>Open Source</span>
                                <span class="model-version-tag">GPT-OSS</span>
                                <span v-if="models === 'Open Source (GPT-OSS)'" class="mdi mdi-check"></span>
                            </button>
                            <button class="dropdown-item" @click="models = 'Deep Analysis (o3)'" :class="{ active: models === 'Deep Analysis (o3)' }">
                                <span>Deep Analysis</span>
                                <span class="model-version-tag">o3</span>
                                <span v-if="models === 'Deep Analysis (o3)'" class="mdi mdi-check"></span>
                            </button>
                            <button class="dropdown-item" @click="models = 'Best knowledge (GPT-4o)'" :class="{ active: models === 'Best knowledge (GPT-4o)' }">
                                <span>Best Knowledge</span>
                                <span class="model-version-tag">GPT-4o</span>
                                <span v-if="models === 'Best knowledge (GPT-4o)'" class="mdi mdi-check"></span>
                            </button>
                        </div>
                    </v-menu>

                    <!-- Active Features Chips -->
                    <div v-if="deepResearch" class="chip">
                        <span>Deep Research</span>
                        <button @click="toggleDeepResearch">&times;</button>
                    </div>
                    <div v-if="use_flowcase_bot" class="chip">
                        <span>References</span>
                        <button @click="toggleReferenceBot">&times;</button>
                    </div>
                    <div v-if="use_company_context_addon" class="chip">
                        <span>Context</span>
                        <button @click="toggleCompanyContextAddon">&times;</button>
                    </div>
                    <div v-if="use_pptx_agent" class="chip">
                        <span>PowerPoint</span>
                        <button @click="togglePptxAgent">&times;</button>
                    </div>
                    <div v-if="fileupload && !use_pptx_agent" class="chip file">
                        <span class="mdi mdi-file-pdf-box"></span>
                        <span>{{ filename }}</span>
                        <button @click="onRemoveFile">&times;</button>
                    </div>
                    <div v-if="fileupload && use_pptx_agent" class="chip file pptx-context">
                        <span class="mdi mdi-file-pdf-box"></span>
                        <span>{{ filename }} (Kontext)</span>
                        <button @click="onRemoveFile">&times;</button>
                    </div>
                </div>

                <div class="input-container">
                    <v-menu location="top start" :close-on-content-click="true">
                        <template v-slot:activator="{ props }">
                            <button v-bind="props" class="icon-btn plus-btn">
                                <span class="mdi mdi-plus"></span>
                            </button>
                        </template>
                        <div class="dropdown-menu">
                            <button class="dropdown-item" @click="toggleDeepResearch">
                                <span class="mdi mdi-magnify-scan"></span>
                                <span>Deep Research</span>
                            </button>
                            <button v-if="has_flowcase_addon" class="dropdown-item" @click="toggleReferenceBot">
                                <span class="mdi mdi-chart-box-outline"></span>
                                <span>References Bot</span>
                            </button>
                            <button class="dropdown-item" @click="toggleCompanyContextAddon">
                                <span class="mdi mdi-link-variant"></span>
                                <span>Company Context</span>
                            </button>
                            <button class="dropdown-item" @click="togglePptxAgent">
                                <span class="mdi mdi-file-powerpoint-outline"></span>
                                <span>PowerPoint Agent</span>
                            </button>
                            <div class="dropdown-divider"></div>
                            <button class="dropdown-item" @click="onUploadFile">
                                <span class="mdi mdi-paperclip"></span>
                                <span>Upload PDF</span>
                            </button>
                        </div>
                    </v-menu>

                    <textarea
                        v-model="input"
                        placeholder="Message..."
                        :disabled="isPrintingText"
                        @keydown.enter.exact.prevent="sendMessage"
                        @input="autoResize"
                        rows="1"
                    ></textarea>

                    <button v-if="!isPrintingText" class="icon-btn send-btn" @click="sendMessage" :disabled="!input.trim()">
                        <span class="mdi mdi-arrow-up"></span>
                    </button>
                    <button v-else class="icon-btn stop-btn" @click="stopTyping">
                        <span class="mdi mdi-stop"></span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Disclaimer -->
        <v-dialog v-model="overlayDisclaimer" fullscreen>
            <v-card class="disclaimer-dialog mobile">
                <div class="disclaimer-header">
                    <h2>Disclaimer</h2>
                    <button class="close-btn" @click="overlayDisclaimer = false">
                        <span class="mdi mdi-close"></span>
                    </button>
                </div>
                <div class="disclaimer-body">
                    <p class="lang-note">English version below</p>

                    <h3>1. Verwendungszweck und Status</h3>
                    <ul>
                        <li>Dieser digitale Assistent (Chatbot) wird von HPC bereitgestellt, um Mitarbeitende bei Recherchen, Ideenfindung und Textbausteinen zu unterstützen.</li>
                        <li>Das System befindet sich in einer Test-/Pilotphase und kann bei Bedarf eingeschränkt oder abgeschaltet werden.</li>
                    </ul>

                    <h3>2. Teilweise Web-Recherche</h3>
                    <ul>
                        <li>Um aktuelle Informationen einzuholen, nutzt der Chatbot bei Bedarf Bing Search innerhalb von Microsoft Azure. Dabei werden nur relevante Teilinhalte des Prompts an die Such-API übermittelt.</li>
                        <li>Eine vollständige Weitergabe der gesamten Unterhaltung an die Web-Suche erfolgt nicht.</li>
                    </ul>

                    <h3>3. Zulässige Eingaben</h3>
                    <ul>
                        <li><strong>Erlaubt:</strong> Interne Projektnamen, Kunden-/Kollegen-Namen, geschäftliche Daten, soweit für die Arbeit erforderlich.</li>
                        <li><strong>Bitte meiden:</strong> Hochsensible Daten (gemäß DSGVO Art. 9), z. B. Gesundheitsangaben, politische oder religiöse Überzeugungen u. Ä.</li>
                        <li><strong>Achtung bei streng vertraulichen Themen:</strong> Geben Sie hier nur das Nötigste ein.</li>
                    </ul>

                    <h3>4. Datenschutz & Verarbeitung</h3>
                    <ul>
                        <li><strong>Keine dauerhafte Speicherung:</strong> Eingaben verbleiben maximal 30 Tage in Azure-Logs.</li>
                        <li><strong>Keine Trainingsnutzung:</strong> Die Eingaben dienen nicht dem KI-Modelltraining.</li>
                        <li><strong>DSGVO-Konformität:</strong> HPC hat mit Microsoft Azure entsprechende Verträge (DPA).</li>
                    </ul>

                    <h3>5. Haftungsausschluss & Inhaltliche Grenzen</h3>
                    <ul>
                        <li><strong>Automatisierte Antworten:</strong> Texte können fehlerhaft, veraltet oder unvollständig sein.</li>
                        <li><strong>Keine verbindliche Beratung:</strong> Alle Ausgaben sind unverbindlich.</li>
                        <li><strong>Haftung:</strong> HPC haftet nicht für Schäden aus Chatbot-Inhalten.</li>
                    </ul>

                    <h3>6. Web-Inhalte und Urheberrecht</h3>
                    <ul>
                        <li>Bei Web-Recherchen können urheberrechtlich geschützte Texte einfließen.</li>
                        <li>Eine kommerzielle Weiterverwendung liegt in der Verantwortung der Nutzenden.</li>
                    </ul>

                    <h3>7. Security & Missbrauch</h3>
                    <ul>
                        <li>Alle Datenverbindungen sind abgesichert, Restrisiko verbleibt.</li>
                        <li>Verstöße sind untersagt. HPC behält sich vor, Zugriffe zu sperren.</li>
                    </ul>

                    <h3>8. Konformität mit KI-Act und Data Act</h3>
                    <ul>
                        <li>HPC berücksichtigt relevante Vorgaben des KI-Acts und EU Data Acts.</li>
                        <li>Aktuell keine Einstufung als „High Risk".</li>
                    </ul>

                    <h3>9. Änderungen und Kommunikation</h3>
                    <ul>
                        <li>HPC kann diesen Disclaimer jederzeit anpassen.</li>
                    </ul>

                    <h3>10. Kontakt</h3>
                    <p class="contact-block">
                        Datenschutzbeauftragter: Dr. John Maurer<br>
                        Bei St. Annen 1, 20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>Mit der Nutzung stimmen Sie den Bedingungen zu.</strong></p>

                    <hr>

                    <h3>1. Purpose and Status</h3>
                    <ul>
                        <li>This chatbot is provided by HPC to support employees in research and idea generation.</li>
                        <li>The system is in a test/pilot phase.</li>
                    </ul>

                    <h3>2. Partial Web Search</h3>
                    <ul>
                        <li>The chatbot uses Bing Search within Microsoft Azure when needed.</li>
                        <li>Only relevant parts of the prompt are transmitted.</li>
                    </ul>

                    <h3>3. Permitted Inputs</h3>
                    <ul>
                        <li><strong>Allowed:</strong> Internal project names, business data as required.</li>
                        <li><strong>Please avoid:</strong> Highly sensitive data (GDPR Art. 9).</li>
                    </ul>

                    <h3>4. Data Protection & Processing</h3>
                    <ul>
                        <li><strong>No permanent storage:</strong> Max 30 days in Azure logs.</li>
                        <li><strong>No training usage:</strong> Inputs not used for AI training.</li>
                        <li><strong>GDPR compliance:</strong> HPC has DPA with Microsoft Azure.</li>
                    </ul>

                    <h3>5. Liability Disclaimer</h3>
                    <ul>
                        <li>Responses may be incorrect or incomplete.</li>
                        <li>All outputs are non-binding.</li>
                    </ul>

                    <h3>6. Web Content and Copyright</h3>
                    <ul>
                        <li>Web searches may include copyrighted texts.</li>
                        <li>Users are responsible for any reuse.</li>
                    </ul>

                    <h3>7. Security & Misuse</h3>
                    <ul>
                        <li>All data connections are secured.</li>
                        <li>Misuse is prohibited. HPC may restrict access.</li>
                    </ul>

                    <h3>8. AI Act and Data Act Compliance</h3>
                    <ul>
                        <li>HPC considers AI Act and EU Data Act requirements.</li>
                    </ul>

                    <h3>9. Changes</h3>
                    <ul>
                        <li>HPC may adjust this disclaimer at any time.</li>
                    </ul>

                    <h3>10. Contact</h3>
                    <p class="contact-block">
                        Data Protection Officer: Dr. John Maurer<br>
                        Bei St. Annen 1, 20457 Hamburg<br>
                        Phone: +49 40 74008-0<br>
                        E-mail: info@hpc-hamburg.de
                    </p>

                    <p class="agreement-note"><strong>By using this assistant, you agree to the above conditions.</strong></p>
                </div>
                <div class="disclaimer-footer">
                    <button class="primary-btn" @click="overlayDisclaimer = false">I Understand</button>
                </div>
            </v-card>
        </v-dialog>

        <input type="file" ref="fileInput" style="display: none" accept="application/pdf" @change="handleFileUpload" />

        <div v-if="loading" class="loading-overlay">
            <v-progress-circular color="primary" indeterminate size="40"></v-progress-circular>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import config from '../config.js';
import { marked } from 'marked';
import * as XLSX from 'xlsx';

const DEFAULT_TYPING_TEXT = "Thinking";
const WEBSEARCH_TYPING_TEXT = "Searching the web";
const PDF_TYPING_TEXT = "Analysing PDF";
const DEEPRESEARCH_TYPING_TEXT = "Deep Research in progress...";
const SUMMARY_TYPING_TEXT = "Summarizing";
const PROGRESS_REFERENCESBOT = "Consulting References Bot...";
const PROGRESS_COMPANYCONTEXT = "Searching Company Context...";
const PROGRESS_PPTX = "Creating PowerPoint...";

export default {
    props: {
        isMobile: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            input: '',
            messages: [],
            loading: true,
            isTyping: false,
            isPrintingText: false,
            drawer: false,
            selectedChatId: 0,
            chats: [],
            new_thread: true,
            username: '',
            email: '',
            fileupload: false,
            fileprocessing: true,
            filename: '',
            selectedFile: undefined,
            filecontent: '',
            fileerror: false,
            fileerror_message: '',
            overlayDisclaimer: false,
            convertingPdfDone: false,
            isDragging: false,
            modelMenu: false,
            models: 'Auto Reasoning (GPT-5.2)',
            pdf_dialog: false,
            promiseResolve: null,
            pdf_context: false,
            deepResearch: false,
            typingText: DEFAULT_TYPING_TEXT,
            has_flowcase_addon: false,
            use_flowcase_bot: false,
            thread_id: undefined,
            use_company_context_addon: false,
            use_pptx_agent: false,
        };
    },
    mounted() {
        document.title = 'TIS Chat Bot';
        setTimeout(() => {
            this.loading = false;
        }, 500);
    },
    beforeDestroy() {
        if (this.$refs.scrollContainer) {
            const exportButtons = this.$refs.scrollContainer.querySelectorAll('.exportButton');
            exportButtons.forEach(btn => {
                btn.removeEventListener('click', this.exportToExcel);
            });
            const pptxButtons = this.$refs.scrollContainer.querySelectorAll('.pptxDownloadButton');
            pptxButtons.forEach(btn => {
                btn.removeEventListener('click', this.downloadPptx);
            });
        }
    },
    created() {
        this.getUser();
    },
    methods: {
        autoResize(event) {
            const textarea = event.target;
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
        },
        toggleDeepResearch() {
            this.deepResearch = !this.deepResearch;
            if (this.deepResearch) {
                this.use_flowcase_bot = false;
                this.use_company_context_addon = false;
                this.use_pptx_agent = false;
            }
        },
        toggleCompanyContextAddon() {
            this.use_company_context_addon = !this.use_company_context_addon;
            if (this.use_company_context_addon) {
                this.deepResearch = false;
                this.use_flowcase_bot = false;
                this.use_pptx_agent = false;
            }
        },
        toggleReferenceBot() {
            this.use_flowcase_bot = !this.use_flowcase_bot;
            if (this.use_flowcase_bot) {
                this.deepResearch = false;
                this.use_company_context_addon = false;
                this.use_pptx_agent = false;
            }
        },
        togglePptxAgent() {
            this.use_pptx_agent = !this.use_pptx_agent;
            if (this.use_pptx_agent) {
                this.deepResearch = false;
                this.use_flowcase_bot = false;
                this.use_company_context_addon = false;
            }
        },
        onRemoveFile() {
            this.fileupload = false;
            this.filename = '';
            this.selectedFile = undefined;
            this.filecontent = '';
            this.fileprocessing = false;
            this.fileerror_message = '';
            this.convertingPdfDone = false;
        },
        onUploadFile() {
            this.$refs.fileInput.click();
        },
        handleFileError(message) {
            this.selectedFile = undefined;
            this.filename = '';
            this.fileupload = false;
            this.filecontent = '';
            this.fileprocessing = false;
            this.fileerror_message = message;
        },
        async handleFileUpload(event) {
            this.fileprocessing = true;
            const file = event.target.files[0];
            this.processFile(file);
        },
        processFile(file) {
            if (file && file.type === 'application/pdf') {
                this.selectedFile = file;
                this.filename = file.name.length > 25 ? file.name.slice(0, 22) + '...' : file.name;
                this.fileupload = true;
                this.convertingPdfDone = true;
                this.fileprocessing = false;
            } else {
                this.handleFileError('Please provide a PDF file');
            }
        },
        onDragOver(event) {
            this.isDragging = true;
        },
        onDragLeave(event) {
            this.isDragging = false;
        },
        onDrop(event) {
            this.isDragging = false;
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.fileprocessing = true;
                this.processFile(files[0]);
            }
        },
        stopTyping() {
            this.isPrintingText = false;
            this.isTyping = false;
        },
        renderMarkdown(text) {
            let html = marked(text);
            html = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
            if (html.includes('<table')) {
                html = html.replace('<table', '<div class="table-wrapper"><table class="data-table"');
                html = html.replace('</table>', '</table><button class="exportButton"><span class="mdi mdi-microsoft-excel"></span> Export</button></div>');
            }
            // Handle PPTX download tags (old format with base64)
            const pptxRegex = /<pptx-download data="([^"]+)" filename="([^"]+)">([^<]+)<\/pptx-download>/g;
            html = html.replace(pptxRegex, (match, data, filename, label) => {
                return `<button class="pptxDownloadButton" data-pptx="${data}" data-filename="${filename}"><span class="mdi mdi-file-powerpoint"></span> ${label}</button>`;
            });
            // Handle new PPTX_FILE format [PPTX_FILE:filename.pptx]
            const pptxFileRegex = /\[PPTX_FILE:([^\]]+)\]/g;
            html = html.replace(pptxFileRegex, (match, filename) => {
                return `<button class="pptxDownloadButton pptxFileDownload" data-filename="${filename}"><span class="mdi mdi-file-powerpoint"></span> Download PowerPoint</button>`;
            });
            return html;
        },
        downloadPptx(event) {
            const btn = event.target.closest('.pptxDownloadButton');
            if (btn) {
                // Check if it's a file download (new format) or base64 (old format)
                if (btn.classList.contains('pptxFileDownload')) {
                    this.downloadPptxFile(btn);
                } else {
                    const base64Data = btn.getAttribute('data-pptx');
                    const filename = btn.getAttribute('data-filename') || 'presentation.pptx';

                    // Convert base64 to blob
                    const byteCharacters = atob(base64Data);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });

                    // Create download link
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }
            }
        },
        async downloadPptxFile(btn) {
            const filename = btn.getAttribute('data-filename');
            if (!filename) return;

            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No token');

                // Fetch file from backend
                const response = await axios.get(`${config.baseURL}/api/v1/download/pptx/${filename}/`, {
                    headers: { Authorization: `Token ${token}` },
                    responseType: 'blob'
                });

                // Create download link
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Error downloading PPTX:', error);
                alert('Download fehlgeschlagen. Bitte versuchen Sie es erneut.');
            }
        },
        async getUser() {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;

                const headers = { Authorization: `Token ${token}` };
                const response = await axios.get(`${config.baseURL}/api/v1/user/me/`, { headers });
                if (response.status === 200) {
                    this.username = response.data.first_name + ' ' + response.data.last_name;
                    this.email = response.data.email;
                }

                const addonsResponse = await axios.get(`${config.baseURL}/api/v1/user/addons/`, { headers });
                if (addonsResponse.status === 200) {
                    this.has_flowcase_addon = addonsResponse.data.has_flowcase_addon;
                }
            } catch (error) {
                console.error('Error fetching user:', error);
            }
        },
        chatScrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.scrollContainer;
                if (container) {
                    const bottom = container.querySelector('.bottom');
                    if (bottom) {
                        bottom.scrollIntoView({ behavior: 'smooth' });
                    }
                }
            });
        },
        wait(ms) {
            return new Promise(resolve => setTimeout(resolve, ms));
        },
        async sendMessage() {
            if (this.input.trim() === '' || this.isPrintingText) return;

            while (this.fileupload && this.fileprocessing) {
                await this.wait(500);
            }

            // Kombiniere Datei und Text in einer einzigen User-Bubble
            if (this.fileupload) {
                const fileHtml = `<div class="file-attachment">
                    <div class="file-icon"><span class="mdi mdi-file-pdf-box"></span></div>
                    <div class="file-info">
                        <span class="file-name">${this.filename}</span>
                        <span class="file-type">PDF Document</span>
                    </div>
                </div>`;
                const messageHtml = this.input ? `<div class="user-text">${this.input}</div>` : '';
                this.messages.push({
                    text: fileHtml + messageHtml,
                    from: 'user'
                });
            } else {
                this.messages.push({ text: this.input, from: 'user' });
            }

            const url = `${config.baseURL}/api/v1/bot/`;
            let chat_id = this.selectedChatId || 1;

            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No token');

                const headers = { headers: { Authorization: `Token ${token}` } };
                const formData = new FormData();
                formData.append('user_message', this.input);
                formData.append('chat_id', chat_id);
                formData.append('email', this.email);
                formData.append('new_thread', this.new_thread);

                if (this.new_thread) {
                    this.thread_id = crypto.randomUUID().replace(/-/g, '');
                }
                formData.append('thread_id', this.thread_id);
                formData.append('model_selection', this.models);
                formData.append('deepResearch', this.deepResearch);
                formData.append('use_flowcase_bot', this.use_flowcase_bot);
                formData.append('use_company_context_addon', this.use_company_context_addon);
                formData.append('use_pptx_agent', this.use_pptx_agent);

                if (this.selectedFile) {
                    formData.append('file', this.selectedFile);
                    // Bei PowerPoint Agent: PDF nur für PPTX-Erstellung, NICHT als Chat-Kontext
                    if (!this.use_pptx_agent) {
                        this.pdf_context = true;
                    }
                }
                formData.append('file_context', this.pdf_context);

                this.new_thread = false;
                this.typingText = DEFAULT_TYPING_TEXT;
                this.isTyping = true;
                this.isPrintingText = true;
                this.chatScrollToBottom();
                this.input = '';

                // Reset textarea height
                if (this.$refs.inputField) {
                    this.$refs.inputField.style.height = 'auto';
                }

                this.onRemoveFile();

                const startRes = await axios.post(url, formData, headers);
                const taskId = startRes.data?.task_id;
                if (!taskId) throw new Error('No task_id');

                while (true) {
                    const statusRes = await axios.get(`${url}?task_id=${encodeURIComponent(taskId)}`, headers);
                    const { state, result } = statusRes.data || {};

                    if (state?.startsWith?.('PROGRESS_') || state === 'PROGRESS' || state === 'STARTED') {
                        const stateMap = {
                            'PROGRESS_WEBSEARCH': WEBSEARCH_TYPING_TEXT,
                            'PROGRESS_PDF': PDF_TYPING_TEXT,
                            'PROGRESS_DEEPRESEARCH': DEEPRESEARCH_TYPING_TEXT,
                            'PROGRESS_SUMMARY': SUMMARY_TYPING_TEXT,
                            'PROGRESS_REFERENCESBOT': PROGRESS_REFERENCESBOT,
                            'PROGRESS_COMPANYCONTEXT': PROGRESS_COMPANYCONTEXT,
                            'PROGRESS_PPTX': PROGRESS_PPTX,
                        };
                        this.typingText = stateMap[state] || DEFAULT_TYPING_TEXT;
                        this.chatScrollToBottom();
                    }

                    if (statusRes.status === 200 && state === 'SUCCESS') {
                        this.pdf_context = result.file_context;
                        this.addMessage(result.result);
                        this.isTyping = false;
                        this.isPrintingText = false;
                        this.chatScrollToBottom();
                        return;
                    }

                    if (state === 'FAILURE') {
                        this.addMessage('An error occurred. Please try again.');
                        this.isTyping = false;
                        this.isPrintingText = false;
                        return;
                    }

                    await this.wait(1000);
                }
            } catch (error) {
                this.addMessage('An error occurred. Please try again.');
                this.isTyping = false;
                this.isPrintingText = false;
                console.error('Error:', error);
            }
        },
        exportToExcel(event) {
            const btn = event.target.closest('.exportButton');
            if (btn) {
                const table = btn.closest('.table-wrapper')?.querySelector('.data-table');
                if (table) {
                    const workbook = XLSX.utils.table_to_book(table, { sheet: "Sheet1" });
                    XLSX.writeFile(workbook, 'export.xlsx');
                }
            }
        },
        addMessage(message) {
            this.messages.push({ text: this.renderMarkdown(message), from: 'bot' });
            this.isTyping = false;
            this.isPrintingText = false;
        },
    },
    watch: {
        messages: {
            handler() {
                this.$nextTick(() => {
                    this.chatScrollToBottom();
                    if (this.$refs.scrollContainer) {
                        const exportButtons = this.$refs.scrollContainer.querySelectorAll('.exportButton');
                        exportButtons.forEach(btn => btn.addEventListener('click', this.exportToExcel));

                        const pptxButtons = this.$refs.scrollContainer.querySelectorAll('.pptxDownloadButton');
                        pptxButtons.forEach(btn => btn.addEventListener('click', this.downloadPptx));
                    }
                });
            },
            deep: true,
        },
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* ============================================
   Light Theme Chat Interface
   ============================================ */

/* Base App Container */
.chat-app {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 64px);
    width: 100%;
    background: #ffffff;
    color: #1a1a1a;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    bottom: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow: hidden;
}

.chat-app.mobile {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    height: auto;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* Drag & Drop Overlay */
.chat-app.drag-over::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(14, 165, 233, 0.08);
    border: 3px dashed #0ea5e9;
    border-radius: 12px;
    z-index: 1000;
    pointer-events: none;
}

.chat-app.drag-over::after {
    content: 'Drop PDF here';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #0ea5e9;
    color: white;
    padding: 16px 32px;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 500;
    z-index: 1001;
    pointer-events: none;
    box-shadow: 0 8px 32px rgba(14, 165, 233, 0.3);
}

/* Welcome Screen */
.welcome-screen {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 180px;
}

.welcome-content {
    text-align: center;
    padding: 40px;
}

.welcome-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: white;
    font-size: 36px;
}

.welcome-title {
    font-size: 32px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 12px;
}

.welcome-subtitle {
    font-size: 18px;
    color: #64748b;
    margin: 0;
    font-weight: 400;
}

.chat-app.mobile .welcome-screen {
    flex: 1;
    padding-bottom: 0;
}

.chat-app.mobile .welcome-avatar {
    width: 60px;
    height: 60px;
    font-size: 30px;
    margin-bottom: 20px;
}

.chat-app.mobile .welcome-title {
    font-size: 26px;
}

.chat-app.mobile .welcome-subtitle {
    font-size: 16px;
}

/* Main Chat Area */
.chat-main {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    padding-bottom: 180px;
    overflow: hidden;
}

/* Messages Area */
.messages-area {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    min-width: 100%;
    overscroll-behavior: contain;
}

.messages-inner {
    max-width: 1200px;
    min-width: min(1200px, 100%);
    width: 100%;
    margin: 0 auto;
    padding: 24px 40px 0;
    box-sizing: border-box;
}

.messages-inner .bottom {
    height: 40px;
    margin: 0;
    padding: 0;
}

.chat-app.mobile .messages-area {
    flex: 1 1 0;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    max-height: 100%;
    -webkit-overflow-scrolling: touch;
}

.chat-app.mobile .messages-inner {
    max-width: 100%;
    padding: 16px 12px;
    margin: 0;
    padding-bottom: 24px;
}

.chat-app.mobile .messages-inner .bottom {
    display: none;
}

.chat-app.mobile .message-row {
    padding: 6px 0;
}

.chat-app.mobile .message-row.user .message-text {
    max-width: 95%;
    margin-right: 4px;
}

.chat-app.mobile .message-row.assistant .message-text {
    max-width: 100%;
}

.chat-app.mobile .avatar {
    width: 28px;
    height: 28px;
    min-width: 28px;
    font-size: 16px;
}

.chat-app.mobile .message-content {
    gap: 8px;
}

/* Message Row */
.message-row {
    padding: 8px 0;
    display: flex;
}

.message-row:first-child {
    padding-top: 0;
}

.message-row:last-of-type {
    padding-bottom: 0;
}

.message-row.user {
    justify-content: flex-end;
}

.message-row.assistant {
    justify-content: flex-start;
}

.message-content {
    display: flex;
    gap: 12px;
    max-width: 100%;
    text-align: left;
}

.message-row.user .message-content {
    flex-direction: row-reverse;
    text-align: left;
}

/* Avatar */
.avatar {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
}

/* Message Text */
.message-text {
    line-height: 1.5;
    font-size: 15px;
    color: #1a1a1a;
    text-align: left;
}

.message-row.user .message-text {
    background: #0ea5e9;
    color: white;
    padding: 12px 18px;
    border-radius: 20px 20px 4px 20px;
    text-align: left;
    max-width: 80%;
    margin-right: 12px;
}

.message-row.assistant .message-text {
    padding: 4px 0;
    text-align: left;
    max-width: 100%;
}

/* Typing Indicator */
.typing-indicator {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    color: #64748b;
    font-size: 14px;
}

.typing-text {
    font-weight: 500;
}

.typing-dots {
    display: flex;
    gap: 4px;
}

.typing-dots span {
    width: 6px;
    height: 6px;
    background: #0ea5e9;
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) { animation-delay: 0.2s; }
.typing-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
    40% { transform: translateY(-6px); opacity: 1; }
}

/* Input Area */
.input-area {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 24px;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
    z-index: 100;
}

.mobile-input-area {
    padding: 12px 8px;
    flex-shrink: 0;
    background: #ffffff;
    border-top: 1px solid #e2e8f0;
}

.input-wrapper {
    max-width: 1200px;
    margin: 0 auto;
}

.chat-app.mobile .input-wrapper,
.mobile-input-area .input-wrapper {
    max-width: 100%;
}

/* Input Toolbar */
.input-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.toolbar-model-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    color: #1e293b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
}

.toolbar-model-btn:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.toolbar-model-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.toolbar-model-btn .mdi {
    font-size: 16px;
    color: #64748b;
}

.toolbar-model-btn .mdi-chevron-down {
    font-size: 18px;
    margin-left: -2px;
}

.toolbar-model-btn .model-version {
    color: #64748b;
    font-weight: 400;
    font-size: 12px;
}

.chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    font-size: 13px;
    color: #475569;
}

.chip .mdi {
    color: #0ea5e9;
}

.chip.file {
    background: #ecfdf5;
    border-color: #a7f3d0;
    color: #047857;
}

.chip.file .mdi {
    color: #10b981;
}

.chip.file.pptx-context {
    background: #fef3c7;
    border-color: #fcd34d;
    color: #92400e;
}

.chip.file.pptx-context .mdi {
    color: #f59e0b;
}

.chip button {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    margin-left: 4px;
    transition: color 0.15s;
}

.chip button:hover {
    color: #1e293b;
}

/* Input Container */
.input-container {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    padding: 8px 8px 8px 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: border-color 0.2s, box-shadow 0.2s;
}

.input-container:focus-within {
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.input-container textarea {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #1a1a1a;
    font-size: 15px;
    line-height: 1.5;
    resize: none;
    max-height: 200px;
    padding: 4px 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.input-container textarea::placeholder {
    color: #94a3b8;
}

.input-container textarea:disabled {
    opacity: 0.5;
}

/* Icon Buttons */
.icon-btn {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    max-width: 36px !important;
    max-height: 36px !important;
    border-radius: 50% !important;
    border: none;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    font-size: 20px;
    flex-shrink: 0;
    flex-grow: 0;
    padding: 0 !important;
    box-sizing: border-box;
}

.icon-btn:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.icon-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.send-btn {
    background: #0ea5e9;
    color: white;
}

.send-btn:hover:not(:disabled) {
    background: #0284c7;
}

.send-btn:disabled {
    background: #e2e8f0;
    color: #94a3b8;
}

.stop-btn {
    background: #f1f5f9;
    color: #64748b;
}

.stop-btn:hover {
    background: #e2e8f0;
    color: #475569;
}

/* Input Footer */
.input-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 12px;
    color: #94a3b8;
}

.input-footer .separator {
    color: #cbd5e1;
}

.input-footer .link {
    color: #64748b;
    cursor: pointer;
    transition: color 0.15s;
}

.input-footer .link:hover {
    color: #0ea5e9;
}

/* Dropdown Menu */
.dropdown-menu {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 8px 0;
    min-width: 220px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    background: transparent;
    border: none;
    color: #1e293b;
    font-size: 14px;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
}

.dropdown-item:hover {
    background: #f8fafc;
}

.dropdown-item.active {
    color: #0ea5e9;
    background: #f0f9ff;
}

.dropdown-item .mdi {
    font-size: 18px;
    color: #64748b;
}

.dropdown-item.active .mdi {
    color: #0ea5e9;
}

.dropdown-item .mdi-check {
    margin-left: auto;
    color: #0ea5e9;
}

.dropdown-divider {
    height: 1px;
    background: #e2e8f0;
    margin: 8px 0;
}

/* Model Menu */
.model-menu {
    min-width: 280px;
}

.model-menu .dropdown-item {
    padding: 14px 16px;
}

.model-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
}

.model-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.model-name {
    font-weight: 500;
    color: #1e293b;
}

.model-version-tag {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
}

.dropdown-item.active .model-version-tag {
    background: #e0f2fe;
    color: #0284c7;
}

.model-desc {
    font-size: 12px;
    color: #64748b;
}

/* Disclaimer Dialog */
.disclaimer-dialog {
    background: #ffffff !important;
    color: #1e293b;
    border-radius: 20px !important;
    overflow: hidden;
}

.disclaimer-dialog.mobile {
    border-radius: 0 !important;
}

.disclaimer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
}

.disclaimer-header h2 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: #1e293b;
}

.close-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
}

.close-btn:hover {
    background: #e2e8f0;
    color: #1e293b;
}

.disclaimer-body {
    padding: 24px;
    font-size: 14px;
    line-height: 1.5;
    max-height: 60vh;
    overflow-y: auto;
}

.disclaimer-body .lang-note {
    text-align: center;
    color: #94a3b8;
    margin-bottom: 20px;
    font-size: 13px;
}

.disclaimer-body h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 20px 0 8px;
    color: #1e293b;
}

.disclaimer-body h3:first-of-type {
    margin-top: 0;
}

.disclaimer-body ul {
    margin: 0 0 0 20px;
    padding: 0;
    color: #475569;
}

.disclaimer-body li {
    margin-bottom: 6px;
}

.disclaimer-body hr {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 24px 0;
}

.disclaimer-body .contact {
    text-align: center;
    color: #64748b;
    margin-top: 20px;
}

.disclaimer-body .contact-block {
    background: #f8fafc;
    padding: 16px;
    border-radius: 8px;
    color: #475569;
    line-height: 1.6;
    margin: 12px 0;
}

.disclaimer-body .agreement-note {
    text-align: center;
    color: #1e293b;
    margin: 20px 0 0;
    padding: 16px;
    background: #f0f9ff;
    border-radius: 8px;
    border-left: 4px solid #0ea5e9;
}

.disclaimer-footer {
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
}

.primary-btn {
    width: 100%;
    padding: 14px 24px;
    background: #0ea5e9;
    border: none;
    border-radius: 12px;
    color: white;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
}

.primary-btn:hover {
    background: #0284c7;
}

/* Loading Overlay */
.loading-overlay {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

/* Scrollbar */
.messages-area::-webkit-scrollbar {
    width: 8px;
}

.messages-area::-webkit-scrollbar-track {
    background: transparent;
}

.messages-area::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.messages-area::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Message Content Styles */
:deep(.message-text) {
    white-space: normal;
    word-wrap: break-word;

    h1, h2, h3 {
        margin: 20px 0 8px;
        font-weight: 600;
        color: #1e293b;
        line-height: 1.3;
    }
    h1:first-child, h2:first-child, h3:first-child {
        margin-top: 0;
    }
    h1 { font-size: 1.5em; }
    h2 { font-size: 1.3em; }
    h3 { font-size: 1.1em; }

    p {
        margin: 14px 0;
        line-height: 1.7;
    }
    p:first-child {
        margin-top: 0;
    }
    p:last-child {
        margin-bottom: 0;
    }

    ul, ol {
        margin: 12px 0 12px 24px !important;
        padding: 0 0 0 16px !important;
        line-height: 1.6;
        list-style-position: outside !important;
    }

    ul {
        list-style-type: disc !important;
    }

    ol {
        list-style-type: decimal !important;
    }

    li {
        margin: 4px 0;
        padding-left: 4px;
        display: list-item !important;
    }

    li p {
        margin: 4px 0;
        display: inline;
    }

    ul ul, ol ol, ul ol, ol ul {
        margin: 4px 0 4px 20px !important;
    }

    a {
        color: #0ea5e9;
        text-decoration: none;
    }

    a:hover {
        text-decoration: underline;
    }

    code {
        background: #f1f5f9;
        color: #e11d48;
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 13px;
        font-family: 'SF Mono', Monaco, Consolas, monospace;
    }

    pre {
        background: #1e293b;
        color: #e2e8f0;
        padding: 16px;
        border-radius: 12px;
        overflow-x: auto;
        margin: 20px 0;
        line-height: 1.5;
        white-space: pre;
    }

    pre code {
        background: transparent;
        color: inherit;
        padding: 0;
        white-space: pre;
    }

    blockquote {
        border-left: 3px solid #0ea5e9;
        margin: 20px 0;
        padding: 12px 20px;
        background: #f8fafc;
        border-radius: 0 12px 12px 0;
        color: #475569;
    }

    hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin: 12px 0;
        opacity: 0.5;
    }

    strong, b {
        font-weight: 600;
        color: #1e293b;
    }

    em, i {
        font-style: italic;
    }

    .table-wrapper {
        overflow-x: auto;
        margin: 20px 0;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
        border-radius: 8px;
        overflow: hidden;
    }

    .data-table th,
    .data-table td {
        padding: 14px 20px;
        text-align: left;
        border-bottom: 1px solid #e2e8f0;
        white-space: nowrap;
    }

    .data-table th {
        background: #0ea5e9;
        color: white;
        font-weight: 500;
        padding: 16px 20px;
    }

    .data-table tr:hover {
        background: #f8fafc;
    }

    .data-table td:first-child,
    .data-table th:first-child {
        padding-left: 24px;
    }

    .data-table td:last-child,
    .data-table th:last-child {
        padding-right: 24px;
    }

    .exportButton {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 12px;
        padding: 10px 18px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        color: #475569;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
    }

    .exportButton:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
    }

    .exportButton .mdi {
        color: #10b981;
        font-size: 18px;
    }

    .pptxDownloadButton {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
        padding: 12px 24px;
        background: linear-gradient(135deg, #d35230 0%, #b7472a 100%);
        border: none;
        border-radius: 10px;
        color: white;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
        box-shadow: 0 2px 8px rgba(211, 82, 48, 0.3);
    }

    .pptxDownloadButton:hover {
        background: linear-gradient(135deg, #b7472a 0%, #9c3d24 100%);
        box-shadow: 0 4px 12px rgba(211, 82, 48, 0.4);
        transform: translateY(-1px);
    }

    .pptxDownloadButton .mdi {
        font-size: 20px;
    }

    .file-msg {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .file-msg .mdi {
        font-size: 20px;
    }

    /* File Attachment in User Message */
    .file-attachment {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 12px;
        margin-bottom: 10px;
        border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .file-attachment .file-icon {
        width: 40px;
        height: 40px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .file-attachment .file-icon .mdi {
        font-size: 22px;
        color: #ffffff;
    }

    .file-attachment .file-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .file-attachment .file-name {
        font-size: 14px;
        font-weight: 500;
        color: #ffffff;
    }

    .file-attachment .file-type {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .user-text {
        margin-top: 2px;
    }
}
</style>

<style>
/* Unscoped styles for v-html content - Lists */
.message-text p {
    margin: 14px 0 !important;
    line-height: 1.7 !important;
}

.message-text p:first-child {
    margin-top: 0 !important;
}

.message-text p:last-child {
    margin-bottom: 0 !important;
}

.message-text ul,
.message-text ol {
    margin: 14px 0 14px 0 !important;
    padding-left: 40px !important;
    line-height: 1.7 !important;
}

.message-text ul {
    list-style-type: disc !important;
}

.message-text ol {
    list-style-type: decimal !important;
}

.message-text li {
    margin: 4px 0 !important;
    display: list-item !important;
    list-style-position: outside !important;
}

.message-text ul ul,
.message-text ol ol,
.message-text ul ol,
.message-text ol ul {
    margin: 4px 0 4px 0 !important;
    padding-left: 24px !important;
}

.message-text hr {
    border: none !important;
    border-top: 1px solid #e5e7eb !important;
    margin: 8px 0 !important;
    opacity: 0.6 !important;
    height: 0 !important;
}

.message-text .exportButton,
.message-row .message-text .exportButton,
.chat-app .exportButton,
button.exportButton {
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #475569 !important;
    border: 1px solid #e2e8f0 !important;
}

.message-text .table-wrapper,
.chat-app .table-wrapper {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
}

.message-text .exportButton,
.chat-app .exportButton,
button.exportButton {
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #475569 !important;
    border: 1px solid #e2e8f0 !important;
    margin-top: 20px !important;
}

.message-text .exportButton:hover,
.chat-app .exportButton:hover,
button.exportButton:hover {
    background: #f8fafc !important;
    background-color: #f8fafc !important;
}

/* PowerPoint Download Button */
.message-text .pptxDownloadButton,
.chat-app .pptxDownloadButton,
button.pptxDownloadButton {
    display: inline-flex !important;
    align-items: center !important;
    gap: 8px !important;
    margin-top: 16px !important;
    padding: 12px 24px !important;
    background: linear-gradient(135deg, #d35230 0%, #b7472a 100%) !important;
    border: none !important;
    border-radius: 10px !important;
    color: white !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: all 0.15s !important;
    box-shadow: 0 2px 8px rgba(211, 82, 48, 0.3) !important;
}

.message-text .pptxDownloadButton:hover,
.chat-app .pptxDownloadButton:hover,
button.pptxDownloadButton:hover {
    background: linear-gradient(135deg, #b7472a 0%, #9c3d24 100%) !important;
    box-shadow: 0 4px 12px rgba(211, 82, 48, 0.4) !important;
}

.message-text .pptxDownloadButton .mdi,
.chat-app .pptxDownloadButton .mdi {
    font-size: 20px !important;
}

/* Tabellen-Styles für v-html */
.message-text .table-wrapper {
    overflow-x: auto !important;
    margin: 20px 0 !important;
}

.message-text .data-table,
.message-text table {
    width: 100% !important;
    border-collapse: collapse !important;
    font-size: 14px !important;
    border-radius: 8px !important;
}

.message-text .data-table th,
.message-text .data-table td,
.message-text table th,
.message-text table td {
    padding: 16px 24px !important;
    text-align: left !important;
    border-bottom: 1px solid #e2e8f0 !important;
}

.message-text .data-table th,
.message-text table th {
    background: #0ea5e9 !important;
    color: white !important;
    font-weight: 500 !important;
    padding: 18px 24px !important;
}

.message-text .data-table tr:hover,
.message-text table tr:hover {
    background: #f8fafc !important;
}

/* File Attachment Styles for v-html (unscoped) */
.message-text .file-attachment,
.message-row.user .file-attachment {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 12px 14px !important;
    background: rgba(255, 255, 255, 0.15) !important;
    border-radius: 12px !important;
    margin-bottom: 10px !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.message-text .file-attachment .file-icon,
.message-row.user .file-attachment .file-icon {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    background: rgba(255, 255, 255, 0.2) !important;
    border-radius: 8px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.message-text .file-attachment .file-icon .mdi,
.message-row.user .file-attachment .file-icon .mdi {
    font-size: 22px !important;
    color: #ffffff !important;
}

.message-text .file-attachment .file-info,
.message-row.user .file-attachment .file-info {
    display: flex !important;
    flex-direction: column !important;
    gap: 2px !important;
}

.message-text .file-attachment .file-name,
.message-row.user .file-attachment .file-name {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #ffffff !important;
}

.message-text .file-attachment .file-type,
.message-row.user .file-attachment .file-type {
    font-size: 11px !important;
    color: rgba(255, 255, 255, 0.7) !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
}

.message-text .user-text,
.message-row.user .user-text {
    margin-top: 2px !important;
}
</style>
